import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { toast } from 'material-react-toastify';

import TabSwitcher from '@/components/Tabs/Tabs';
import { Text } from '@/components/shared/Text/Text';
import { TextField } from '@/components/shared/TextField/TextField';
import WithLoading from '@/lib/hoc/WithLoading/WithLoading';
import { UserConversion } from '@addmile/backend-client';

import QuizResults from './components/QuizResults';
import Sessions from './components/Sessions/Sessions';
import UserBilling from './components/UserBilling/UserBilling';
import UserProfile from './components/UserProfile/UserProfile';
import { useUserChatBalance } from './hooks/useUserChatBalance';
import { useUserProfile } from './hooks/useUserProfile';
import { useUserSubscriptions } from './hooks/useUserSubscription';

enum UserTabs {
  General = 'General',
  QuizResults = 'Quiz Results',
  Sessions = 'Sessions',
  Billing = 'Billing',
}

const UserData = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchEmail, setSearchEmail] = useState(() => {
    return searchParams.get('email') || '';
  });

  const [activeEmail, setActiveEmail] = useState<string | null>(() => {
    return searchParams.get('email');
  });

  const {
    data: userData,
    isLoading: isUserLoading,
    error: userError,
  } = useUserProfile(activeEmail);

  const { data: subscriptions = [], isLoading: isSubscriptionsLoading } = useUserSubscriptions(
    userData?.user?.id,
  );

  const { data: balance = 0, isLoading: isBalanceLoading } = useUserChatBalance(userData?.user?.id);

  const user = userData?.user;
  const conversion = userData?.conversion;
  const sessionsData = userData?.sessions;

  useEffect(() => {
    const emailFromUrl = searchParams.get('email');
    if (emailFromUrl) {
      setSearchEmail(decodeURIComponent(emailFromUrl));
      setActiveEmail(decodeURIComponent(emailFromUrl));
    }
  }, [searchParams]);

  useEffect(() => {
    if (userError) {
      toast.error(`Error fetching user data: ${userError}`);
    }
  }, []);

  const handleSearch = useCallback(() => {
    const trimmedEmail = searchEmail.trim();
    if (!trimmedEmail) {
      toast.error('Please enter an email address');
      return;
    }

    setActiveEmail(trimmedEmail);
    setSearchParams({ email: trimmedEmail });
  }, [searchEmail, setSearchParams]);

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearField = () => {
    setSearchEmail('');
    setActiveEmail(null);
    setSearchParams({});
  };

  const tabs = {
    [UserTabs.General]: user && (
      <UserProfile
        user={user}
        conversion={conversion as UserConversion}
        balance={balance as number}
      />
    ),
    [UserTabs.QuizResults]: conversion?.quiz && <QuizResults quiz={conversion?.quiz} />,
    [UserTabs.Sessions]: sessionsData && <Sessions sessions={sessionsData} />,
    [UserTabs.Billing]: subscriptions.length > 0 && <UserBilling subscriptions={subscriptions} />,
  };

  const isLoading = isUserLoading || isSubscriptionsLoading || isBalanceLoading;

  return (
    <WithLoading isLoading={isLoading} className="flex flex-col w-full">
      <Text.H4 weight="bold">User Data</Text.H4>
      <div className="relative mt-4 w-[576px] mb-12">
        <TextField
          placeholder="Search User"
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          onKeyUp={handleKeyUp}
          className="w-full"
          startIcon={<img src={'/icons/search.svg'} alt="search" width={24} height={24} />}
          endIcon={
            <button onClick={handleClearField}>
              <img src={'/icons/close.svg'} alt="search" width={24} height={24} />
            </button>
          }
        />
      </div>

      {user && (
        <TabSwitcher
          tabs={tabs}
          tabKeys={[
            { label: 'General', key: UserTabs.General },
            { label: 'Quiz Results', key: UserTabs.QuizResults },
            { label: 'Sessions', key: UserTabs.Sessions },
            { label: 'Billing', key: UserTabs.Billing },
          ]}
        />
      )}
    </WithLoading>
  );
};

export default UserData;
